/**
 * The one thing she may say without being spoken to.
 *
 * Everything else in this neighbourhood — greetings, log reactions, chip
 * comebacks, petting — answers something the user just did. Ambient is the
 * opposite: a stare at an empty day left untouched (`Today.tsx`), or a nod
 * to the kettle's evening shift (`PresenceController.tsx`). PERSONA.md calls
 * it "Netikėtos akimirkos" and gives it three hard rules this module
 * enforces: at most once a day, sassy register only, and never on a cooled
 * day — an unprompted remark from someone she is not currently speaking to
 * would read as a provocation, not overheard household chatter.
 *
 * The daily ration follows the exact Dexie-meta pattern `interaction.ts`
 * uses for its own daily counters: one row per account
 * (`glitime.ambient.<owner>`), mirrored in a synchronous in-memory cache
 * because the two producers that call this cannot await a database
 * round-trip before deciding whether to speak.
 *
 * `fireAmbient` is the shared body of both producers. A due follow-up she
 * has not managed to ask about yet today outranks either pool — see
 * PERSONA.md's "Rūpestis" — so whichever producer reaches this first on a
 * given day surfaces the follow-up if one is waiting, and only falls back to
 * its own catalogue draw when there isn't one. `takeFollowUpAsk` already
 * guards against asking the same follow-up twice, which is what makes it
 * safe to call from either producer without the two coordinating directly.
 */

import type { SassEvent } from '@gramas/shared'
import { getMeta, setMeta } from '../db/dexie.js'
import { register, sassLine } from '../sass/engine.js'
import { currentProfile } from '../screens/useProfile.js'
import { showSassToast } from '../shell/toastStore.js'
import { herToday } from './clock.js'
import { dueFollowUp, takeFollowUpAsk } from './followups.js'
import { affectionNow } from './interaction.js'

interface AmbientState {
  /** Local date the `used` flag below is for. */
  date: string
  used: boolean
}

const EMPTY: AmbientState = { date: '', used: false }

const ANON_OWNER = 'anon'

function ownerKey(): string {
  return currentProfile()?.id ?? ANON_OWNER
}

function metaKey(owner: string): string {
  return `glitime.ambient.${owner}`
}

/** Her day, not the device's — see `clock.ts`. */
function localDate(now: Date): string {
  return herToday(now)
}

const cache = new Map<string, AmbientState>()
const hydrationPromises = new Map<string, Promise<void>>()

function hydrate(owner: string): Promise<void> {
  let promise = hydrationPromises.get(owner)
  if (promise == null) {
    promise = getMeta<AmbientState>(metaKey(owner), EMPTY).then((state) => {
      if (!cache.has(owner)) cache.set(owner, state)
    })
    hydrationPromises.set(owner, promise)
  }
  return promise
}

/** Today's state, reset if the stored day is over. */
function read(owner: string, now: Date): AmbientState {
  void hydrate(owner)
  const state = cache.get(owner) ?? EMPTY
  const today = localDate(now)
  if (state.date === today) return state
  return { date: today, used: false }
}

function write(owner: string, state: AmbientState): void {
  cache.set(owner, state)
  void setMeta(metaKey(owner), state)
}

/**
 * Today's unprompted-word slot, spent at most once. False (and a no-op)
 * when it is already spent today, the register is not „Aštri", or the
 * account is currently cooled — she does not volunteer chatter to someone
 * she just slammed a door on.
 */
export function takeAmbientSlot(now: Date = new Date()): boolean {
  if (register() !== 'sassy') return false
  if (affectionNow(now) === 'cooled') return false
  const owner = ownerKey()
  const state = read(owner, now)
  if (state.used) return false
  write(owner, { date: localDate(now), used: true })
  return true
}

/**
 * Attempt to spend today's ambient slot on one of the two ambient events.
 * No-op (the slot stays free) whenever `takeAmbientSlot` refuses. Otherwise
 * a pending follow-up wins the slot over the event's own catalogue draw —
 * see the header for why that ordering needs no coordination between the
 * two producers that call this.
 */
export function fireAmbient(
  event: Extract<SassEvent, 'ambient_stare' | 'ambient_kettle'>,
  hour: number,
  now: Date = new Date(),
): void {
  if (!takeAmbientSlot(now)) return
  const followUp = dueFollowUp()
  if (followUp != null) {
    const taken = takeFollowUpAsk()
    if (taken != null) {
      showSassToast(taken.ask)
      return
    }
  }
  const line = sassLine(event, { hour }, Math.random, now.getTime())
  if (line != null) showSassToast(line)
}

/** Test-only: drop the cache so the next read starts cold. */
export function __resetAmbientForTests(): void {
  cache.clear()
  hydrationPromises.clear()
}

/** Test-only: wait for an account's state to finish loading. */
export function __hydrateAmbientForTests(owner: string): Promise<void> {
  return hydrate(owner)
}
